import styled from '@emotion/styled';
import { Grid, Paper, Tooltip, Typography } from '@material-ui/core';
import ArrowForward from '@material-ui/icons/ArrowForward';
import Save from '@material-ui/icons/Save';
import { debounce } from 'lodash-es';
import { createElement, FC, Fragment, useContext, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useMount } from 'react-use';
import { useRecoilValue } from 'recoil';

import ProjectTitleEditor from '../../../component/compound/BasicProjectSettingEditor/ProjectTitleEditor';
import CreativeBriefEditor from '../../../component/compound/CreativeBriefEditor';
import Spinner from '../../../component/compound/Spinner';
import SubmitDialog from '../../../component/compound/SubmitDialog';
import ButtonWithIcon from '../../../component/primitive/ButtonWithIcon';
import { SCREEN_LARGE } from '../../../constant/pageWidth';
import { SPACING_HUGE, SPACING_REGULAR, SPACING_UNIT } from '../../../constant/spacing';
import { context as DependencyContext } from '../../../context/dependency';
import { context as StateContext } from '../../../context/state';
import { flexibleLayoutContainer } from '../../../styleUtility/flexibleLayout';
import mq from '../../../styleUtility/mediaquery';
import {
  editingProjectAssetFilesStateAtom,
  editingProjectStateAtom,
  editingProjectStateErrorsAtom,
} from '../atoms/editingProjectStateAtom';
import { projectAtom, projectLoadingAtom } from '../atoms/projectAtom';
import { hasAnyErrors } from '../domain/stateValidator';
import { useEditingProjectState } from '../hooks/useEditingProjectState';
import useUpdateProject from '../hooks/useUpdateProject';
import AssetFormErrorMessages from './CreativeBriefScreen/AssetFormErrorMessages';
import CreativeBriefAssetFilesBlock from './CreativeBriefScreen/CreativeBriefAssetFilesBlock';

type Props = {
  setConfirmAlert: (confirmAlert: boolean) => void;
};

const EditSubmittedScreen: FC<Props> = ({ setConfirmAlert }) => {
  const { assetFiles } = useRecoilValue(editingProjectAssetFilesStateAtom);
  const project = useRecoilValue(projectAtom);
  const isLoading = useRecoilValue(projectLoadingAtom);
  const state = useRecoilValue(editingProjectStateAtom);
  const errors = useRecoilValue(editingProjectStateErrorsAtom);
  const { language } = useContext(StateContext);
  const { updateSubmittedProject, saveDraftEditOfSubmittedProject } = useUpdateProject();
  const { setTitle, deleteUnnecessaryAssetFiles, updateAssetCategoryValue } = useEditingProjectState();
  const {
    editProjectUsecase: { setAssetFormTermAgreements },
  } = useContext(DependencyContext);

  const { creativeBriefValue, assetCategoryValue } = state;

  const [isOpenDialog, setIsOpenDialog] = useState(false);

  useMount(async () => {
    await setAssetFormTermAgreements();
  });

  return (
    <Root>
      <Grid container direction="row" justifyContent="flex-start" alignItems="stretch" spacing={4}>
        <Grid item xs={12} sm={12} md={12} lg={12}>
          {isLoading || !creativeBriefValue || project?.projectType == null || !errors ? (
            <Spinner />
          ) : (
            <>
              {/* Project Name */}
              <Heading variant="h5">
                <FormattedMessage id="project_title_editor.title" />
              </Heading>
              <SupportingText variant="body2" color="textSecondary">
                <FormattedMessage id="project_title_editor.description" />
              </SupportingText>
              <Container>
                <ProjectTitleEditor
                  title={project?.title || ''}
                  projectType={project?.projectType}
                  language={language}
                  errors={errors}
                  onChangeTitle={debounce((title) => setTitle(title), 250)}
                />
              </Container>
              {/* Creative Brief */}
              <Heading variant="h5">
                <FormattedMessage id="project_editing_page.creative_brief.description.main" />
              </Heading>
              <SupportingText variant="body2" color="textSecondary">
                <FormattedMessage id="project_editing_page.creative_brief.description.sub" />
              </SupportingText>
              <CreativeBriefEditor project={project} />
            </>
          )}
        </Grid>
      </Grid>

      {!isLoading && assetCategoryValue && project?.bucketId && project?.creativeBriefDefinition ? (
        <CreativeBriefAssetFilesBlock
          bucketId={project?.bucketId}
          assetCategoryValue={assetCategoryValue}
          onChangeAssetCategoryValue={debounce((_, value) => updateAssetCategoryValue(value), 250)}
        />
      ) : null}

      {!isLoading && project?.creativeBriefDefinition && creativeBriefValue && errors ? (
        <>
          <AssetFormErrorMessages errors={errors} />
          <Actions>
            <Buttons>
              <ButtonWithIcon
                icon={Save}
                color="default"
                variant="contained"
                onClick={() => {
                  setConfirmAlert(false);
                  setTimeout(async () => {
                    await deleteUnnecessaryAssetFiles(project, [...assetFiles]);
                    saveDraftEditOfSubmittedProject({ state });
                  }, 0);
                }}
              >
                <FormattedMessage id="project_editing_page.creative_submitted.buttons.save_draft" />
              </ButtonWithIcon>
              <FormattedMessage id="project_editing_page.for_filling_out.helper_text">
                {(txt: string) => {
                  const invalid = hasAnyErrors(errors);
                  return (
                    <Tooltip
                      placement="top"
                      title={txt}
                      disableFocusListener={invalid}
                      disableHoverListener={invalid}
                      disableTouchListener={invalid}
                    >
                      <ButtonWithIcon
                        icon={ArrowForward}
                        variant="contained"
                        color="primary"
                        disabled={invalid}
                        onClick={() => setIsOpenDialog(true)}
                      >
                        <FormattedMessage id="project_editing_page.creative_submitted.buttons.submit" />
                      </ButtonWithIcon>
                    </Tooltip>
                  );
                }}
              </FormattedMessage>
            </Buttons>
          </Actions>
        </>
      ) : null}

      <SubmitDialog
        onClickAcceptButton={() => {
          setConfirmAlert(false);
          setTimeout(async () => {
            await deleteUnnecessaryAssetFiles(project, assetFiles);
            await updateSubmittedProject({ state });
          }, 0);
        }}
        open={isOpenDialog}
        onRequestClose={() => setIsOpenDialog(false)}
      />
    </Root>
  );
};

const Root = styled('section')`
  width: auto;
  max-width: ${SCREEN_LARGE}px;
  margin-top: ${SPACING_HUGE}px;
  margin-right: auto;
  margin-left: auto;
  padding-bottom: ${SPACING_UNIT * 12}px;

  ${mq('sp')} {
    padding-left: ${SPACING_REGULAR}px;
    padding-right: ${SPACING_REGULAR}px;
    padding-bottom: ${SPACING_UNIT * 6}px;
  }

,
`;

const Heading = styled(Typography)`
  margin-bottom: ${SPACING_UNIT}px;
`;

const SupportingText = styled(Typography)`
  margin-bottom: ${SPACING_REGULAR}px;
`;

const Container = styled(Paper)`
  margin-top: ${SPACING_REGULAR}px;
  margin-bottom: ${SPACING_HUGE}px;
  padding: ${SPACING_REGULAR}px;
`;

const Actions = styled('div')`
  ${flexibleLayoutContainer({
    justifyContent: 'flex-end',
  })}
  margin-top: 46px;
`;

const Buttons = styled('div')`
  > button {
    margin-left: ${SPACING_UNIT}px;
  }
`;

export default EditSubmittedScreen;
